import { useQuery } from "@tanstack/react-query";
import axiosInstance, { endpoints } from "../../services/axios";
import { useSearchParams } from "react-router-dom";
import { get } from "lodash";
import {
  App,
  Button,
  Dropdown,
  Flex,
  Image,
  MenuProps,
  message,
  Table,
  Typography,
} from "antd";
import { ISurvey } from "../../models";
import { DeleteOutlined, UserOutlined } from "@ant-design/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import { useBoolean } from "../../hooks/use-boolean";
import AddMdDialog from "./add-md-dialog";
import { useMemo } from "react";

interface Props {
  permissions: Record<string, boolean>;
}

const MdTab = ({ permissions }: Props) => {
  const { modal } = App.useApp();
  const [searchParams] = useSearchParams();
  const modalBool = useBoolean();

  const {
    data: mdData,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["md-list"],
    queryFn: async () => {
      axiosInstance.defaults.headers.common.Authorization =
        searchParams.get("token");
      return await axiosInstance.get(endpoints.md.list);
    },
  });

  const { data: mdCompanyData } = useQuery({
    queryKey: ["md-company"],
    queryFn: async () => {
      axiosInstance.defaults.headers.common.Authorization =
        searchParams.get("token");
      return await axiosInstance.get(endpoints.md.company);
    },
  });

  const companyList = useMemo(() => {
    const companiesData = get(mdCompanyData, "data.data.companies", {});
    return Object.values(companiesData).map((item: any) => ({
      value: item.id,
      key: item.id,
      label: item.name || "",
    }));
  }, [mdCompanyData]);

  const departmentList = useMemo(() => {
    const departmentsData = get(mdCompanyData, "data.data.departments", {});
    return Object.values(departmentsData).map((item: any) => ({
      id: item.id,
      name: item.name || "",
    }));
  }, [mdCompanyData]);

  const md: any[] = get(mdData, "data.data", []);

  const confirmDelete =
    (row: { bxUserId: string; bxCompanyId: string }) => () => {
      modal.confirm({
        title: "Delete manager",
        content: "Are you sure you want to delete this MD?",
        onOk: async () =>
          await axiosInstance
            .delete(endpoints.md.delete, { data: row })
            .then(() => {
              refetch();
              message.success("MD successfully deleted!");
            })
            .catch(() => ""),

        okText: "Yes, delete",
        cancelText: "No, cancel",
      });
    };

  const columns = [
    {
      title: "№",
      dataIndex: "no",
      render: (_: any, i: any, index: number) => index + 1,
      width: 30,
    },
    {
      title: "Rasm",
      width: 80,
      render: (value: any) => (
        <>
          {value?.photo ? (
            <Image
              className="rounded-full !w-[60px] !h-[60px] object-cover"
              src={value?.photo}
              alt={value?.name}
            />
          ) : (
            <div className="w-[50px] h-[50px] flex justify-center items-center rounded-full bg-gray-100">
              <UserOutlined style={{ fontSize: 32 }} />
            </div>
          )}
        </>
      ),
    },
    {
      title: "F.I.SH",
      dataIndex: "fio",
      key: "fio",
      width: 300,
    },
    // {
    //   title: "Name",
    //   dataIndex: "name",
    //   key: "name",
    // },
    // {
    //   title: "Last Name",
    //   dataIndex: "lastName",
    //   key: "lastName",
    // },
    // {
    //   title: "Patron",
    //   dataIndex: "patron",
    //   key: "patron",
    // },
    {
      title: "Bo'lim",
      dataIndex: "departmentIds",
      render: (value: any) =>
        departmentList
          .filter((item) => value?.includes(item.id))
          .map((item: any) => <span key={item.id}>{item.name}</span>),
    },
    {
      title: "Kompaniya",
      dataIndex: ["company", "name"],
      key: "company",
      width: 200,
    },
    {
      title: "",
      dataIndex: "actions",
      key: "id",
      align: "right" as "right",
      width: 50,

      render: (_: any, row: ISurvey) => {
        const items: MenuProps["items"] = [];

        if (permissions?.canDeleteMD)
          items.push({
            key: "delete",
            label: "Delete",
            onClick: confirmDelete({
              bxUserId: get(row, "id", ""),
              bxCompanyId: get(row, "company.id", ""),
            }),
            icon: <DeleteOutlined />,
            danger: true,
          });

        return (
          <Dropdown menu={{ items }} placement="bottomRight">
            <Button size="small" type="text" icon={<BsThreeDotsVertical />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div>
      <Flex align="center" justify="space-between" className="mb-2">
        <Typography.Title className="mt-0" level={4}>
          MD
        </Typography.Title>
        {get(permissions, "canAddMD") && (
          <Button type="primary" onClick={modalBool.onTrue}>
            + Add MD
          </Button>
        )}
      </Flex>

      <Table
        bordered
        loading={isFetching}
        rowKey={"id"}
        pagination={false}
        dataSource={md}
        columns={columns}
      />

      <AddMdDialog
        refetch={refetch}
        modalBool={modalBool}
        companyList={companyList}
      />
    </div>
  );
};
export default MdTab;
